/**
 * InviteLandingPage - Emotional landing page for partner invite links
 * Public route - no auth required to view
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Heart, Globe, MapPin, Sparkles, Play, Users, Loader2, AlertTriangle, User
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "@/App";
import DuoCelebration from "@/components/premium/DuoCelebration";

const DUO_FEATURES = [
  { icon: Globe, title: "Shared World Heatmap", desc: "See where you've both been — and where to go next" },
  { icon: Sparkles, title: "Compatibility Score", desc: "Discover your travel style match" },
  { icon: Play, title: "Yearly Replay Together", desc: "Relive your adventures month by month" },
  { icon: Users, title: "Couple Dashboard", desc: "Your combined stats, shared streaks, and milestones" },
];

export default function InviteLandingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, token: authToken } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    fetchInvite();
  }, [token]);

  const fetchInvite = async () => {
    try {
      const res = await axios.get(`${API}/invite/info/${token}`);
      setInvite(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "This invite link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = () => {
    // Store invite token for auto-link after auth
    localStorage.setItem("duolyst_invite_token", token);

    if (user) {
      // Already logged in — accept directly
      acceptInvite();
    } else {
      // Route to register with invite context
      navigate(`/register?invite=${token}`);
    }
  };

  const handleLogin = () => {
    localStorage.setItem("duolyst_invite_token", token);
    navigate(`/login?invite=${token}`);
  };

  const acceptInvite = async () => {
    setAccepting(true);
    try {
      const res = await axios.post(
        `${API}/invite/accept`,
        { token },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setAccepted(res.data);
      localStorage.removeItem("duolyst_invite_token");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to accept invite");
    } finally {
      setAccepting(false);
    }
  };

  // Already accepted — show Duo celebration
  if (accepted) {
    return (
      <DuoCelebration
        partnerName={accepted.partner_name}
        hasDuoAccess={accepted.has_duo_access}
        onDismiss={() => navigate("/dashboard")}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h1 className="text-xl font-serif font-semibold text-foreground mb-2">Invite Unavailable</h1>
        <p className="text-muted-foreground text-center mb-6">{error}</p>
        <Button onClick={() => navigate("/")} variant="outline">Go to DuoLyst</Button>
      </div>
    );
  }

  const { inviter, stats } = invite;
  const avatarSrc = inviter.profile_picture_path
    ? `${API}/profile/picture/${inviter.profile_picture_path}`
    : inviter.avatar_url || null;

  return (
    <div className="min-h-screen bg-background" data-testid="invite-landing">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-pink-500/10 via-rose-500/5 to-transparent" />
        <div className="relative max-w-md mx-auto px-6 pt-12 pb-8 text-center">
          {/* Inviter Avatar */}
          <div className="relative w-24 h-24 mx-auto mb-4">
            <div className="absolute -inset-1 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full animate-pulse opacity-30" />
            {avatarSrc ? (
              <img src={avatarSrc} alt={inviter.name} className="w-24 h-24 rounded-full object-cover border-4 border-card relative z-10" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center border-4 border-card relative z-10">
                <User className="w-12 h-12 text-primary" />
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-pink-500 flex items-center justify-center z-20 border-2 border-card">
              <Heart className="w-4 h-4 text-white" />
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-2">
            {inviter.name} wants to
          </h1>
          <p className="text-lg sm:text-xl font-serif text-pink-500 font-semibold mb-3">
            build a world together
          </p>
          <p className="text-sm text-muted-foreground mb-2">
            {stats.completed_adventures} adventures completed · {stats.countries_visited} countries explored
          </p>
        </div>
      </div>

      {/* Feature Teasers */}
      <div className="max-w-md mx-auto px-6 pb-6">
        <div className="grid grid-cols-2 gap-3 mb-8">
          {DUO_FEATURES.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <Card key={idx} className="border-pink-500/10 bg-pink-500/5">
                <CardContent className="p-3 text-center">
                  <Icon className="w-6 h-6 text-pink-500 mx-auto mb-2" />
                  <p className="text-xs font-medium text-foreground">{feature.title}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{feature.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="space-y-3">
          <Button
            onClick={handleJoin}
            disabled={accepting}
            className="w-full h-12 bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90 text-white text-base font-semibold rounded-xl"
            data-testid="join-partner-btn"
          >
            {accepting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Heart className="w-5 h-5 mr-2" />
                Join {inviter.name} on DuoLyst
              </>
            )}
          </Button>

          {!user && (
            <Button variant="ghost" onClick={handleLogin} className="w-full text-sm text-muted-foreground" data-testid="login-instead-btn">
              Already have an account? Log in
            </Button>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Free to join · No credit card required
        </p>
      </div>
    </div>
  );
}
